import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import '@/style/pdf-lite.css';
import { formatSafeDate, formatCoverDateRange } from "@/lib/date-utils";
import { mapQuotationDatesFromDb, resolveCoverDates } from "@/lib/quotation-display-dates";
import { tripService } from '@/lib/supabase/trips';
import { 
  Quotation, 
  QuotationPage, 
  FlightInfo as BaseFlightInfo,
  AccommodationInfo,
  TourInfo,
  TransportationInfo,
  RestaurantInfo
} from "@/components/quotation/types";
import { quotationService } from '@/lib/supabase/quotations';
import { Download, Loader2 } from 'lucide-react';

// Interface estendida para adicionar propriedades adicionais
interface FlightInfo extends BaseFlightInfo {
  title?: string;
  route?: string;
}

export default function QuotationViewLite() {
  const { id } = useParams<{ id: string }>();
  const [quotation, setQuotation] = useState<Quotation | null>(null);
  const [linkedTrip, setLinkedTrip] = useState<{ start_date?: string | null; end_date?: string | null } | null>(null);
  const [loading, setLoading] = useState(true);

  const coverDateRange = useMemo(() => {
    if (!quotation) return '';
    const { startDate, endDate } = resolveCoverDates(quotation, linkedTrip);
    return formatCoverDateRange(startDate, endDate);
  }, [quotation, linkedTrip]);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // Carregar cotação
  useEffect(() => {
    const loadQuotation = async () => {
      try {
        if (id && id !== 'preview') {
          const quotationData = await quotationService.getQuotationById(id);
          
          if (quotationData) {
            console.log('Carregando cotação para versão lite:', quotationData);
            
            const processedQuotation: any = {
              id: quotationData.id,
              title: quotationData.title || '',
              client: quotationData.client || '',
              destination: quotationData.destination || [],
              ...mapQuotationDatesFromDb(quotationData.start_date, quotationData.end_date),
              status: 'draft',
              notes: quotationData.notes || '',
              pages: quotationData.pages || [],
              coverImages: quotationData.cover_images || [],
              createdAt: quotationData.created_at ? new Date(quotationData.created_at) : new Date(),
              updatedAt: quotationData.updated_at ? new Date(quotationData.updated_at) : new Date(),
              tripId: quotationData.trip_id,
              showRecommendationsTab: quotationData.show_recommendations_tab !== undefined 
                ? quotationData.show_recommendations_tab 
                : true,
              showPriceTab: quotationData.show_price_tab !== undefined 
                ? quotationData.show_price_tab 
                : true,
              recommendationsTabName: quotationData.recommendations_tab_name,
              recommendationGroups: quotationData.recommendation_groups || [],
              recommendations: [],
              showAccommodationsTab: quotationData.show_accommodations_tab !== false,
              accommodationsTabName: quotationData.accommodations_tab_name,
              accommodationsDescription: quotationData.accommodations_description || '',
              showFlightsTab: quotationData.show_flights_tab !== false,
              flightsTabName: quotationData.flights_tab_name,
              flightsDescription: quotationData.flights_description || '',
              priceTabName: quotationData.price_tab_name,
              priceDetails: quotationData.price_details || '',
            };
            
            setQuotation(processedQuotation);
            if (processedQuotation.tripId) {
              const tripData = await tripService.getTripById(processedQuotation.tripId);
              setLinkedTrip(tripData);
            } else {
              setLinkedTrip(null);
            }
            setLoading(false);
            return;
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error in loadQuotation:', error);
        setLoading(false);
      }
    };

    loadQuotation();
  }, [id]);

  // Função para gerar PDF
  const generatePDF = async () => {
    if (!quotation) return;
    
    setGeneratingPDF(true);
    
    try {
      console.log('🔄 Iniciando geração do PDF...');
      
      const response = await fetch('http://localhost:3001/generate-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          quotationData: quotation
        })
      });
      
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }
      
      // Obter o PDF como blob
      const pdfBlob = await response.blob();
      
      // Criar URL para download
      const url = window.URL.createObjectURL(pdfBlob);
      
      // Criar link de download
      const link = document.createElement('a');
      link.href = url;
      link.download = `cotacao-${quotation.id || 'voah'}.pdf`;
      
      // Simular clique para download
      document.body.appendChild(link);
      link.click();
      
      // Limpar
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ PDF gerado e baixado com sucesso!');
      
    } catch (error) {
      console.error('❌ Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF. Verifique se o serviço está rodando.');
    } finally {
      setGeneratingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-voah-teal mx-auto"></div>
          <p className="mt-4 text-voah-teal">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Cotação não encontrada</h2>
          <p className="mb-6 text-gray-600">A cotação que você está procurando não está disponível.</p>
        </div>
      </div>
    );
  }

  // Format destination for display
  const destinationDisplay = typeof quotation.destination === 'string' 
    ? quotation.destination 
    : quotation.destination.join(', ');
    
  // Function to get client names either from the string or from the linked trip
  const formatClientNames = (clientsString: string | undefined) => {
    if (quotation.tripId) {
      try {
        const savedTrips = localStorage.getItem('voah_trips');
        if (savedTrips) {
          const trips = JSON.parse(savedTrips);
          const linkedTrip = trips.find((trip: any) => trip.id === quotation.tripId);
          
          if (linkedTrip && linkedTrip.clientIds && linkedTrip.clientIds.length > 0) {
            const savedClients = localStorage.getItem('voah_clients');
            if (savedClients) {
              const clients = JSON.parse(savedClients);
              const tripClients = clients.filter((client: any) => 
                linkedTrip.clientIds.includes(client.id)
              );
              
              return tripClients
                .map((client: any) => client.name)
                .join(', ');
            }
          }
        }
      } catch (error) {
        // Error silenciado
      }
    }
    
    return clientsString || '';
  };

  // Extrair todos os voos de todas as páginas
  const getAllFlights = quotation.pages?.reduce((allFlights, page) => {
    const pageFlights = (page.flights || []).map(flight => ({
      ...flight,
      _page: page.title || `Página ${page.id.substring(0, 8)}`
    }));
    return [...allFlights, ...pageFlights];
  }, [] as (FlightInfo & { _page?: string })[]) || [];

  // Extrair todas as acomodações de todas as páginas
  const getAllAccommodations = quotation.pages?.reduce((allAccommodations, page) => {
    const pageAccommodations = (page.accommodations || []).map(accommodation => ({
      ...accommodation,
      _page: page.title || `Página ${page.id.substring(0, 8)}`
    }));
    return [...allAccommodations, ...pageAccommodations];
  }, [] as (AccommodationInfo & { _page?: string })[]) || [];

  return (
    <div className="min-h-screen bg-white pdf-lite-container relative">
      {/* Botão flutuante para gerar PDF */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={generatePDF}
          disabled={generatingPDF}
          className={`
            flex items-center gap-2 px-4 py-3 rounded-full shadow-lg text-white font-medium transition-all duration-200
            ${generatingPDF 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-voah-teal hover:bg-voah-teal/90 hover:scale-105 active:scale-95'
            }
          `}
          title={generatingPDF ? 'Gerando PDF...' : 'Baixar PDF'}
        >
          {generatingPDF ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Gerando...</span>
            </>
          ) : (
            <>
              <Download className="w-5 h-5" />
              <span>Baixar PDF</span>
            </>
          )}
        </button>
      </div>

      {/* Estilos específicos para impressão com otimização de imagens */}
      <style>{`
        @media print {
          .print-break {
            page-break-before: always;
          }
          .print-avoid-break {
            page-break-inside: avoid;
          }
          @page {
            margin: 1.5cm 1cm 1.5cm 1cm !important;
            size: A4;
          }
          body {
            margin: 0 !important;
            padding: 0 !important;
          }
          
          /* Otimização drástica de imagens para PDF */
          img {
            width: 100% !important;
            height: auto !important;
            max-height: 192px !important;
            object-fit: cover !important;
            image-rendering: optimizeSpeed !important;
            image-rendering: -moz-crisp-edges !important;
            image-rendering: -webkit-optimize-contrast !important;
            image-rendering: optimize-contrast !important;
            -ms-interpolation-mode: nearest-neighbor !important;
          }
          
          /* Logo específico - manter tamanho controlado */
          .header-logo {
            width: auto !important;
            max-width: 120px !important;
            height: 32px !important;
            max-height: 32px !important;
          }
          
          /* Reduzir qualidade de imagens */
          img {
            image-resolution: 72dpi !important;
          }
        }
        
        /* Estilos para tela também otimizados */
        .pdf-lite-container img {
          width: 100%;
          height: auto;
          max-height: 192px;
          object-fit: cover;
          image-rendering: optimizeSpeed;
          image-rendering: -moz-crisp-edges;
          image-rendering: -webkit-optimize-contrast;
          image-rendering: optimize-contrast;
          -ms-interpolation-mode: nearest-neighbor;
          image-resolution: 72dpi;
        }
        
        /* Logo específico - manter tamanho controlado */
        .pdf-lite-container .header-logo {
          width: auto;
          max-width: 120px;
          height: 32px;
          max-height: 32px;
          image-resolution: 72dpi;
        }
      `}</style>

      {/* Cabeçalho simples */}
      <div className="max-w-4xl mx-auto px-6">
        <div className="py-4 border-b-2 border-voah-teal mb-6">
          {/* Logo e informações em linha separada */}
          <div className="flex items-center justify-between mb-4">
            <img src="/lovable-uploads/3aedb010-4564-4186-8687-ad2d8f6d7160.png" alt="VOAH Logo" className="header-logo" />
            <div className="text-right text-sm text-gray-600">
              <div>{destinationDisplay}</div>
              {coverDateRange && (
                <div>{coverDateRange}</div>
              )}
            </div>
          </div>
          
          {/* Título centralizado da mesma forma que os títulos das páginas */}
          <div className="text-center">
            <h1 className="text-3xl font-bold text-voah-teal mb-2">{quotation.title}</h1>
            <p className="text-sm text-gray-600">{formatClientNames(quotation.client)}</p>
          </div>
        </div>
      </div>

      {/* Conteúdo principal */}
      <div className="max-w-4xl mx-auto px-6">
        {/* Página Boas Vindas */}
        <div className="print-break">
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-voah-teal mb-2">Boas Vindas</h2>
          </div>
          
          {/* Conteúdo da página Boas Vindas */}
          {!quotation.notes ? (
            <div className="mb-6">
              <div className="bg-gray-100 p-4 rounded-md text-center text-gray-500">
                Sem informações adicionais disponíveis.
              </div>
            </div>
          ) : (
            <div className="mb-6">
              <div 
                className="prose prose-sm max-w-none prose-headings:text-voah-teal prose-headings:font-medium prose-p:text-gray-700 prose-strong:text-gray-800 prose-strong:font-medium prose-a:text-voah-teal prose-a:no-underline hover:prose-a:underline prose-li:text-gray-700 prose-li:marker:text-voah-teal text-left text-[14px] leading-loose"
                dangerouslySetInnerHTML={{ 
                  __html: quotation.notes
                    .replace(/style="[^"]*"/g, '')
                    .replace(/style='[^']*'/g, '')
                    .replace(/<p>/g, '<p class="mb-4 px-2 py-1">')
                    .replace(/<p class="mb-4 px-2 py-1">(\s*)<\/p>/g, '')
                }} 
              />
            </div>
          )}
        </div>

        {/* Renderizar todas as páginas em sequência */}
        {quotation.pages?.filter(page => page.isVisible !== false).map((page, index) => (
          <div key={`page-${page.id}`} className={index > 0 ? 'print-break' : ''}>
            {/* Título da página */}
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-voah-teal mb-2">{page.title}</h2>
              {(page.startDate || page.endDate) && (
                <div className="flex items-center text-sm text-gray-500 mb-4">
                  <span>
                    {formatSafeDate(page.startDate, "dd/MM/yyyy")} 
                    {page.endDate && page.startDate && page.endDate !== page.startDate && (
                      <>
                        {" - "}
                        {formatSafeDate(page.endDate, "dd/MM/yyyy")}
                      </>
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* Descrição da página */}
            {page.description && page.description.replace(/<[^>]*>/g, '').trim() !== '' && (
              <div className="mb-6">
                <div 
                  className="text-gray-700 leading-relaxed prose prose-sm max-w-none text-left"
                  dangerouslySetInnerHTML={{ 
                    __html: page.description
                      .replace(/style=\"[^\"]*\"/g, '')
                      .replace(/style='[^']*'/g, '')
                  }}
                />
              </div>
            )}

            {/* Conteúdo simplificado da página */}
            <PageContentLite page={page} />
          </div>
        ))}

        {/* Página de Hospedagens */}
        {quotation.showAccommodationsTab !== false && (
          <div className="print-break">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-voah-teal mb-2">
                {quotation.accommodationsTabName || 'Hospedagens'}
              </h2>
            </div>
            
            {/* Descrição das hospedagens */}
            {quotation.accommodationsDescription && (
              <div className="mb-6">
                <div 
                  className="text-gray-700 leading-relaxed prose prose-sm max-w-none text-left"
                  dangerouslySetInnerHTML={{ 
                    __html: quotation.accommodationsDescription
                      .replace(/style=\"[^\"]*\"/g, '')
                      .replace(/style='[^']*'/g, '')
                  }}
                />
              </div>
            )}

            {/* Lista simplificada de hospedagens */}
            <div className="space-y-4 mb-8">
              {getAllAccommodations.map((accommodation, index) => (
                <div key={accommodation.id} className="mb-4 border border-gray-200 rounded-lg overflow-hidden print-avoid-break">
                  {/* Título da hospedagem */}
                  <div className="px-4 py-3 bg-gray-50 border-b">
                    <h3 className="text-lg font-bold text-voah-teal text-left">
                      {index + 1}. {accommodation.name}
                    </h3>
                  </div>
                  
                  {/* Conteúdo simplificado da hospedagem */}
                  <div className="p-4">
                    {/* Informações básicas */}
                    <div className="mb-3 bg-gray-50 p-3 rounded border">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {accommodation.location && (
                          <div>
                            <span className="text-xs text-gray-500">Localização</span>
                            <div className="text-sm font-medium">{accommodation.location}</div>
                          </div>
                        )}
                        
                        {accommodation.checkInTime && (
                          <div>
                            <span className="text-xs text-gray-500">Check-in</span>
                            <div className="text-sm font-medium">
                              {accommodation.checkInDate && `${accommodation.checkInDate} `}
                              {accommodation.checkInTime && `${accommodation.checkInTime}`}
                            </div>
                          </div>
                        )}
                        
                        {accommodation.checkOutTime && (
                          <div>
                            <span className="text-xs text-gray-500">Check-out</span>
                            <div className="text-sm font-medium">
                              {accommodation.checkOutDate && `${accommodation.checkOutDate} `}
                              {accommodation.checkOutTime && `${accommodation.checkOutTime}`}
                            </div>
                          </div>
                        )}
                        
                        {accommodation.roomType && (
                          <div>
                            <span className="text-xs text-gray-500">Tipo de Quarto</span>
                            <div className="text-sm font-medium">{accommodation.roomType}</div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Descrição da hospedagem */}
                    {accommodation.description && (
                      <div 
                        className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none text-left"
                        dangerouslySetInnerHTML={{ 
                          __html: accommodation.description
                            .replace(/style=\"[^\"]*\"/g, '')
                            .replace(/style='[^']*'/g, '')
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Página de Voos */}
        {quotation.showFlightsTab !== false && (
          <div className="print-break">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-voah-teal mb-2">
                {quotation.flightsTabName || 'Voos'}
              </h2>
            </div>
            
            {/* Descrição dos voos */}
            {quotation.flightsDescription && (
              <div className="mb-6">
                <div 
                  className="text-gray-700 leading-relaxed prose prose-sm max-w-none text-left"
                  dangerouslySetInnerHTML={{ 
                    __html: quotation.flightsDescription
                      .replace(/style=\"[^\"]*\"/g, '')
                      .replace(/style='[^']*'/g, '')
                  }}
                />
              </div>
            )}

            {/* Lista simplificada de voos */}
            <div className="space-y-4 mb-8">
              {getAllFlights.map((flight, index) => (
                <div key={flight.id} className="mb-4 border border-gray-200 rounded-lg overflow-hidden print-avoid-break">
                  {/* Título do voo */}
                  <div className="px-4 py-3 bg-blue-50 text-blue-700 border-b">
                    <h3 className="text-lg font-bold text-left">
                      {index + 1}. {flight.airline || 'Companhia aérea'} {flight.flightNumber && `• ${flight.flightNumber}`}
                    </h3>
                  </div>
                  
                  {/* Conteúdo simplificado do voo */}
                  <div className="p-4">
                    <div className="bg-white rounded border">
                      {/* Header do voo */}
                      <div className="bg-voah-teal text-white p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-lg font-semibold">
                              {flight.flightNumber ? `Voo ${flight.airline} ${flight.flightNumber}` : (flight.airline || 'Voo')}
                            </h4>
                          </div>
                          {flight.price && (
                            <div className="text-lg font-medium">
                              {flight.price.toLocaleString('pt-BR', { 
                                style: 'currency', 
                                currency: flight.currency || 'BRL' 
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Flight route simplificada */}
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          {/* Departure information */}
                          <div className="text-center flex-1">
                            <div className="text-sm text-gray-500 mb-1">Partida</div>
                            <div className="text-xl font-bold text-voah-teal">
                              {flight.departureAirport}
                            </div>
                            {flight.departureDateTime && (
                              <div className="text-sm text-gray-700">
                                {formatSafeDate(flight.departureDateTime, "dd/MM/yyyy")} • {flight.departureDateTime.split('T')[1]?.substring(0, 5)}
                              </div>
                            )}
                          </div>
                          
                          {/* Arrow */}
                          <div className="flex items-center justify-center px-4">
                            <span className="text-voah-teal text-2xl">→</span>
                          </div>
                          
                          {/* Arrival information */}
                          <div className="text-center flex-1">
                            <div className="text-sm text-gray-500 mb-1">Chegada</div>
                            <div className="text-xl font-bold text-voah-teal">
                              {flight.arrivalAirport}
                            </div>
                            {flight.arrivalDateTime && (
                              <div className="text-sm text-gray-700">
                                {formatSafeDate(flight.arrivalDateTime, "dd/MM/yyyy")} • {flight.arrivalDateTime.split('T')[1]?.substring(0, 5)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Página de Preços */}
        {quotation.showPriceTab && (
          <div className="print-break">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-voah-teal mb-2">
                {quotation.priceTabName || 'Preços'}
              </h2>
            </div>
            
            {/* Conteúdo dos preços */}
            {!quotation.priceDetails ? (
              <div className="mb-6">
                <div className="bg-gray-100 p-4 rounded-md text-center text-gray-500">
                  Sem informações de preço disponíveis.
                </div>
              </div>
            ) : (
              <div className="mb-6">
                <div 
                  className="prose prose-sm max-w-none prose-headings:text-voah-teal prose-headings:font-medium prose-p:text-gray-700 prose-strong:text-gray-800 prose-strong:font-medium prose-a:text-voah-teal prose-a:no-underline hover:prose-a:underline prose-li:text-gray-700 prose-li:marker:text-voah-teal text-left text-[14px] leading-loose"
                  dangerouslySetInnerHTML={{ 
                    __html: quotation.priceDetails
                      .replace(/style=\"[^\"]*\"/g, '')
                      .replace(/style='[^']*'/g, '')
                      .replace(/<p>/g, '<p class="mb-4 px-2 py-1">')
                      .replace(/<p class="mb-4 px-2 py-1">(\s*)<\/p>/g, '')
                  }} 
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Componente simplificado para renderizar o conteúdo de uma página
function PageContentLite({ page }: { page: QuotationPage }) {
  // Extrair dados da página
  const blocks = page.blocks || [];
  const tours = page.tours || [];
  const transportations = page.transportations || [];
  const restaurants = page.restaurants || [];

  // Combinar todos os itens em uma lista simples
  const allItems = [
    ...blocks.map(block => ({ ...block, type: 'block' })),
    ...tours.map(tour => ({ ...tour, type: 'tour' })),
    ...transportations.map(transportation => ({ ...transportation, type: 'transportation' })),
    ...restaurants.map(restaurant => ({ ...restaurant, type: 'restaurant' }))
  ];

  return (
    <div className="space-y-4 mb-8">
      {allItems.map((item: any, index: number) => {
        // Renderizar apenas título, descrição curta e informações-chave
        if (item.type === 'block') {
          return (
            <div key={item.id} className="mb-4 border border-gray-200 rounded-lg overflow-hidden print-avoid-break">
              {/* Título do bloco */}
              <div className="px-4 py-3 bg-gray-50 border-b">
                <h3 className="text-lg font-bold text-voah-teal text-left">
                  {index + 1}. {item.title}
                </h3>
              </div>
              
              {/* Conteúdo completo */}
              <div className="p-4">
                {/* Apenas 1 imagem principal com tamanho reduzido */}
                {item.images && item.images.length > 0 && (
                  <div className="mb-4">
                    <img 
                      src={item.images[0]} 
                      alt={item.title} 
                      className="w-full h-48 object-cover rounded"
                    />
                  </div>
                )}
                
                {/* Descrição completa */}
                {item.description && (
                  <div 
                    className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none text-left"
                    dangerouslySetInnerHTML={{ 
                      __html: item.description
                        .replace(/style=\"[^\"]*\"/g, '')
                        .replace(/style='[^']*'/g, '')
                    }}
                  />
                )}
              </div>
            </div>
          );
        } else if (item.type === 'tour') {
          return (
            <div key={item.id} className="mb-4 border border-gray-200 rounded-lg overflow-hidden print-avoid-break">
              {/* Título do tour */}
              <div className="px-4 py-3 bg-gray-50 border-b">
                <h3 className="text-lg font-bold text-voah-teal text-left">
                  {index + 1}. {item.name}
                </h3>
              </div>
              
              {/* Conteúdo completo */}
              <div className="p-4">
                {/* Apenas 1 imagem principal com tamanho reduzido */}
                {item.images && item.images.length > 0 && (
                  <div className="mb-4">
                    <img 
                      src={item.images[0]} 
                      alt={item.name} 
                      className="w-full h-48 object-cover rounded"
                    />
                  </div>
                )}
                
                {/* Descrição completa */}
                {item.description && (
                  <div 
                    className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none text-left"
                    dangerouslySetInnerHTML={{ 
                      __html: item.description
                        .replace(/style=\"[^\"]*\"/g, '')
                        .replace(/style='[^']*'/g, '')
                    }}
                  />
                )}
              </div>
            </div>
          );
        }
        
        return null;
      })}
    </div>
  );
} 